import React, { useCallback } from "react";
import * as Mui from "@mui/material";
import FieldEditor from "@/components/authoring/FieldEditor";
import { BranchingSection, Section } from "@/types/form";
import DeleteIcon from "@mui/icons-material/Delete";
import { FormAction } from "@/scripts/formAuthoringReducer";
import AddIcon from "@mui/icons-material/Add";
import CallSplitIcon from "@mui/icons-material/CallSplit";

interface SectionEditorProps {
  section: Section;
  editFieldId: string | undefined;
  branchingSections: BranchingSection[];
  isTheOnlyStartingSection: boolean;
  titleFieldId: string | undefined;
  setEditFieldId: (fieldId: string) => void;
  dispatch: React.Dispatch<FormAction>;
}

const SectionEditor: React.FC<SectionEditorProps> = ({
  section,
  editFieldId,
  branchingSections,
  isTheOnlyStartingSection,
  titleFieldId,
  setEditFieldId,
  dispatch,
}) => {
  const handleToggleIsStartingSection = useCallback(() => {
    dispatch({
      type: "TOGGLE_STARTING_SECTION",
      payload: { sectionId: section.id },
    });
  }, [dispatch, section.id]);

  const handleSelectNextSection = useCallback(
    (e: Mui.SelectChangeEvent<string>) => {
      dispatch({
        type: "SET_NEXT_SECTION",
        payload: { sectionId: section.id, nextSectionId: e.target.value },
      });
    },
    [dispatch, section.id]
  );

  const handleRemoveSection = useCallback(() => {
    dispatch({
      type: "REMOVE_SECTION",
      payload: { sectionId: section.id },
    });
  }, [dispatch, section.id]);

  const handleUpdateSectionTitle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({
        type: "UPDATE_SECTION_TITLE",
        payload: { sectionId: section.id, title: e.target.value },
      });
    },
    [dispatch, section.id]
  );

  const handleAddField = useCallback(
    (sectionId: string, fieldId: string | undefined) => {
      dispatch({
        type: "ADD_FIELD",
        payload: { sectionId: sectionId, previousFieldId: fieldId },
      });
    },
    [dispatch]
  );

  const handleSplitSection = useCallback(
    (sectionId: string, fieldId: string) => {
      dispatch({
        type: "SPLIT_SECTION",
        payload: { sectionId, fieldId },
      });
    },
    [dispatch]
  );

  return (
    <Mui.Card key={section.id} className="mb-4" variant="outlined">
      <Mui.CardHeader
        title={
          <Mui.TextField
            value={section.title}
            onChange={handleUpdateSectionTitle}
            placeholder="Section Title"
            variant="outlined"
            fullWidth
          />
        }
      />
      <Mui.CardContent>
        <Mui.Box>
          {section.fields.map((field, index) => {
            const isBranchingField =
              section.branchingRadio?.fieldId === field.id;
            return (
              <>
                <FieldEditor
                  key={field.id}
                  sectionId={section.id}
                  field={field}
                  editEnabled={field.id === editFieldId}
                  isTitleField={
                    field.type === "text" ? titleFieldId === field.id : undefined
                  }
                  isLastField={index === section.fields.length - 1}
                  isFirstField={index === 0}
                  isBranchingField={
                    field.type === "radio" ? isBranchingField : undefined
                  }
                  branchingOptions={
                    isBranchingField
                      ? section.branchingRadio?.branchingOptions
                      : undefined
                  }
                  branchingSections={
                    isBranchingField ? branchingSections : undefined
                  }
                  onSetEditFieldId={setEditFieldId}
                  dispatch={dispatch}
                />
                {field.id === editFieldId && (
                  <Mui.Box className="my-4">
                    <Mui.Button
                      key={`edit-${field.id}`}
                      variant="contained"
                      color="primary"
                      onClick={() => handleAddField(section.id, field.id)}
                      startIcon={<AddIcon />}
                      sx={{ mr: 2 }}
                    >
                      Add Field
                    </Mui.Button>
                    {index < section.fields.length - 1 && (
                      <Mui.Button
                        key={`split-${field.id}`}
                        variant="outlined"
                        color="secondary"
                        onClick={() => handleSplitSection(section.id, field.id)}
                        startIcon={<CallSplitIcon />}
                      >
                        Split Section
                      </Mui.Button>
                    )}
                  </Mui.Box>
                )}
              </>
            );
          })}
          {section.fields.length === 0 && (
            <Mui.Box>
              <Mui.Button
                variant="contained"
                color="primary"
                onClick={() => handleAddField(section.id, undefined)}
                startIcon={<AddIcon />}
              >
                Add Field
              </Mui.Button>
            </Mui.Box>
          )}
          {/* Add Field Button */}
          <Mui.Box display="flex" alignItems="center" justifyContent="flex-end">
            <Mui.Typography>Starting Option</Mui.Typography>
            <Mui.Switch
              checked={section.isStartingSection}
              onChange={handleToggleIsStartingSection}
              disabled={isTheOnlyStartingSection}
              color="primary"
              sx={{ mr: 2 }}
            />
            <Mui.FormControl variant="outlined" size="small">
              <Mui.InputLabel id={`next-section-label-${section.id}`}>
                Next Section
              </Mui.InputLabel>
              <Mui.Select
                labelId={`next-section-label-${section.id}`}
                value={section.nextSectionId || "auto"}
                onChange={handleSelectNextSection}
                label="Next Section"
                className="w-40"
                disabled={section.branchingRadio !== undefined}
              >
                <Mui.MenuItem value="auto">
                  <em>Auto</em>
                </Mui.MenuItem>
                {branchingSections.map((section) => (
                  <Mui.MenuItem key={section.id} value={section.id}>
                    {section.title}
                  </Mui.MenuItem>
                ))}
                <Mui.MenuItem value="end">
                  <em>End</em>
                </Mui.MenuItem>
              </Mui.Select>
            </Mui.FormControl>
            <Mui.IconButton onClick={handleRemoveSection} color="primary">
              <DeleteIcon />
            </Mui.IconButton>
          </Mui.Box>
        </Mui.Box>
      </Mui.CardContent>
    </Mui.Card>
  );
};

export default React.memo(SectionEditor);
